import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { format } from 'date-fns';
import { id as localeId } from 'date-fns/locale';
import RootLanding from '../../components/layouts/RootLanding';
import { BASE_URL, getFetcherPublic } from '../../services/api';
import { BilahCari, GambarAman, LabelSeksi, Paginasi, Wadah } from '../../components/fragments/landing/publik/Elemen';
import { sampulGaleri } from '../../components/fragments/landing/publik/Seksi';
import Ikon from '../../components/fragments/landing/publik/Ikon';
import { TOPIK } from '../../components/fragments/landing/publik/topikPublik';

/*
 * Halaman galeri (frame Figma "Galeri"): album pertama tampil besar di kiri,
 * sisanya kartu tiga kolom. Klik membuka penampil foto/video.
 */
const PER_HALAMAN = 10;

function idVideo(url = '') {
    return url.match(/(?:v=|youtu\.be\/|embed\/)([^&\n?#]+)/)?.[1];
}

function keterangan(g) {
    const tanggal = g.createdAt ? format(new Date(g.createdAt), 'd MMMM yyyy', { locale: localeId }) : '';
    const isi = g.videoLink ? 'Video' : `${g.photoUrls?.length ?? 0} foto`;
    return [tanggal, isi].filter(Boolean).join(' · ');
}

function TombolPutar({ besar = false }) {
    return (
        <span
            className={`absolute left-1/2 top-1/2 inline-flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-pb-aksen ${
                besar ? 'size-16' : 'size-11'
            }`}
        >
            <Ikon nama="putar" className={besar ? 'size-6' : 'size-4'} />
        </span>
    );
}

function KartuAlbum({ g, besar, onBuka }) {
    if (besar) {
        return (
            <button
                type="button"
                onClick={onBuka}
                className="group relative min-h-[320px] overflow-hidden rounded-xl bg-[#dde7e0] text-left md:col-span-2 md:row-span-2"
            >
                <GambarAman src={sampulGaleri(g)} className="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-105" ikonClassName="size-12 text-pb-aksen/40" />
                <span className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-g-700/95" aria-hidden="true" />
                {g.videoLink && <TombolPutar besar />}
                <span className="absolute inset-x-0 bottom-0 flex flex-col gap-1 p-6">
                    <span className="font-body text-lg font-semibold text-white">{g.title}</span>
                    <span className="font-body text-[13px] text-white/80">{keterangan(g)}</span>
                </span>
            </button>
        );
    }
    return (
        <button type="button" onClick={onBuka} className="group flex flex-col overflow-hidden rounded-xl bg-[#f6f7f3] text-left">
            <span className="relative block aspect-[4/3] overflow-hidden bg-[#dde7e0]">
                <GambarAman src={sampulGaleri(g)} className="size-full object-cover transition-transform duration-300 group-hover:scale-105" />
                {g.videoLink && <TombolPutar />}
            </span>
            <span className="flex flex-col gap-1 px-3 py-4">
                <span className="line-clamp-2 font-body text-[15px] font-semibold text-pb-judul">{g.title}</span>
                <span className="font-body text-xs text-pb-teks">{keterangan(g)}</span>
            </span>
        </button>
    );
}

/* Penampil satu album: video (bila ada) lalu foto-fotonya, dengan tombol geser. */
function Penampil({ g, onTutup }) {
    const media = [
        ...(g.videoLink ? [{ jenis: 'video', id: idVideo(g.videoLink) }] : []),
        ...(g.photoUrls ?? []).map((u) => ({ jenis: 'foto', src: `${BASE_URL}${u}` })),
    ];
    const [i, setI] = useState(0);
    const m = media[i];

    useEffect(() => {
        const tombol = (e) => {
            if (e.key === 'Escape') onTutup();
            if (e.key === 'ArrowRight') setI((n) => Math.min(n + 1, media.length - 1));
            if (e.key === 'ArrowLeft') setI((n) => Math.max(n - 1, 0));
        };
        document.addEventListener('keydown', tombol);
        return () => document.removeEventListener('keydown', tombol);
    }, [media.length, onTutup]);

    return (
        <div role="dialog" aria-modal="true" aria-label={g.title} className="fixed inset-0 z-[60] flex flex-col bg-black/90 p-4" onClick={onTutup}>
            <div className="flex items-center justify-between gap-4 text-white" onClick={(e) => e.stopPropagation()}>
                <p className="font-body text-sm">
                    {g.title} {media.length > 1 && <span className="text-white/60">({i + 1}/{media.length})</span>}
                </p>
                <button type="button" onClick={onTutup} aria-label="Tutup" className="inline-flex size-11 items-center justify-center rounded-full hover:bg-white/10">
                    <Ikon nama="tutup" className="size-6" />
                </button>
            </div>
            <div className="flex min-h-0 flex-1 items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                {media.length > 1 && (
                    <button type="button" disabled={i === 0} onClick={() => setI(i - 1)} aria-label="Sebelumnya" className="inline-flex size-11 shrink-0 rotate-180 items-center justify-center rounded-full text-white hover:bg-white/10 disabled:opacity-30">
                        <Ikon nama="panah" className="size-6" />
                    </button>
                )}
                {m?.jenis === 'video' ? (
                    <iframe
                        title={g.title}
                        src={`https://www.youtube.com/embed/${m.id}`}
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="aspect-video w-full max-w-4xl rounded-lg"
                    />
                ) : m ? (
                    <img src={m.src} alt={g.title} className="max-h-full max-w-full rounded-lg object-contain" />
                ) : null}
                {media.length > 1 && (
                    <button type="button" disabled={i === media.length - 1} onClick={() => setI(i + 1)} aria-label="Berikutnya" className="inline-flex size-11 shrink-0 items-center justify-center rounded-full text-white hover:bg-white/10 disabled:opacity-30">
                        <Ikon nama="panah" className="size-6" />
                    </button>
                )}
            </div>
        </div>
    );
}

export default function GaleriPage() {
    const [params, setParams] = useSearchParams();
    const kategori = params.get('kategori');
    const [ketikan, setKetikan] = useState('');
    const [cari, setCari] = useState('');
    const [halaman, setHalaman] = useState(1);
    const [dibuka, setDibuka] = useState(null);

    const { data, isLoading } = useSWR(
        `/api/content/search-gallery?page=${halaman}&limit=${PER_HALAMAN}&sortBy=createdAt${
            kategori ? `&categoryName=${encodeURIComponent(kategori)}` : ''
        }${cari ? `&name=${encodeURIComponent(cari)}` : ''}`,
        getFetcherPublic
    );
    const daftar = data?.data ?? [];

    useEffect(() => setHalaman(1), [kategori, cari]);

    const pilihKategori = (k) => {
        const baru = new URLSearchParams(params);
        if (k) baru.set('kategori', k);
        else baru.delete('kategori');
        setParams(baru, { replace: true });
    };

    return (
        <RootLanding>
            <section className="bg-pb-halaman">
                <Wadah className="flex flex-col gap-8 pb-16 pt-12 lg:px-[120px]">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                        <div className="flex flex-col gap-3">
                            <LabelSeksi oranye>Dokumentasi Kegiatan</LabelSeksi>
                            <h1 className="text-[28px] font-bold tracking-[-0.8px] text-pb-judul md:text-[34px]">
                                Galeri Foto dan Video Pengelolaan B3
                            </h1>
                        </div>
                        <p className="max-w-[420px] font-body text-[15px] text-pb-teks lg:pb-3">
                            Kumpulan dokumentasi kegiatan, sosialisasi, dan program pengelolaan B3.
                        </p>
                    </div>
                    <BilahCari
                        nilai={ketikan}
                        setNilai={setKetikan}
                        onCari={() => setCari(ketikan.trim())}
                        placeholder="Cari berdasarkan judul album"
                        kategori={TOPIK.map((t) => t.kategori)}
                        terpilih={kategori}
                        setTerpilih={pilihKategori}
                    />
                    {isLoading ? (
                        <div className="grid animate-pulse gap-4 md:grid-cols-3">
                            <div className="min-h-[320px] rounded-xl bg-[#dde7e0] md:col-span-2 md:row-span-2" />
                            <div className="h-64 rounded-xl bg-[#dde7e0]" />
                            <div className="h-64 rounded-xl bg-[#dde7e0]" />
                        </div>
                    ) : daftar.length === 0 ? (
                        <p className="rounded-md bg-white p-10 text-center font-body text-sm text-pb-teks">
                            {cari ? 'Tidak ada album yang cocok dengan pencarian Anda.' : 'Belum ada album untuk kategori ini.'}
                        </p>
                    ) : (
                        <div className="grid gap-4 md:grid-cols-3">
                            {daftar.map((g, i) => (
                                <KartuAlbum key={g.id} g={g} besar={i === 0 && halaman === 1} onBuka={() => setDibuka(g)} />
                            ))}
                        </div>
                    )}
                    <Paginasi
                        halaman={halaman}
                        jumlahHalaman={Math.ceil((data?.total ?? 0) / PER_HALAMAN)}
                        setHalaman={setHalaman}
                    />
                </Wadah>
            </section>
            {dibuka && <Penampil g={dibuka} onTutup={() => setDibuka(null)} />}
        </RootLanding>
    );
}
